import React, { useState, useEffect } from 'react'
import _ from 'lodash'
import * as Yup from 'yup'
import { Form, FieldArray, Formik, Field } from 'formik'
import { Autocomplete } from '@material-ui/lab'
import { Box, Typography, TextField, MenuItem, IconButton, Checkbox } from '@material-ui/core'
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline'
import AddCircleOutlineRoundedIcon from '@material-ui/icons/AddCircleOutlineRounded'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxOutlinedIcon from '@material-ui/icons/CheckBoxOutlined'
import { SOMETHING_WENT_WRONG } from '../../Constants/MessagesConstant'
import { vppService, tagService } from '../../Services'
import { AppButton, GroupingCriteria, Loader } from '../../Components'
import { vppForm, allOptions } from './constants'
import { useStyles } from './style'
import { programService } from '../../Services/ProgramService'
import Icon from '../../utils/icons'
import { pushToDataLayer, USERBEHAVIOURTRACKING } from '../../utils/gaEnvironment'
import { utilService } from '../../Services/UtilService/UtilService'

const ALL = 'ALL'
const VppForm = (props) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [stateOptions, setStateOptions] = useState([])
  const [createVppForm, setCreateVppForm] = useState([])
  const [groupingOptions, setGroupingOption] = useState([])
  const [defaultGroupingOptions, setDefaultOption] = useState([])
  const [programId, setProgramId] = useState('')
  const [loading, setLoading] = useState(false)
  const [programList, setProgramList] = useState([])
  const [stateList, setStateList] = useState({ ...props.stateList, ALL: ALL })
  const [program, setProgram] = useState()

  useEffect(() => {
    if (props.stateList)
      setStateList({ ...props.stateList, ALL: ALL })
  }, [props.stateList])

  useEffect(() => {
    props.accountId && programService.getAllAvailablePrograms(props.accountId)
      .then(response => {
        const programs = response ? response.filter(obj => obj.is_active) : []
        setProgramList(programs)
        setLoading(false)
      })
  }, [props.accountId])

  useEffect(() => {
    setProgramId(_.get(props.vppDetails, 'program_id', ''))
  }, [props.isEdit, props.vppDetails])

  useEffect(() => {
    const defaultOptions = _.cloneDeep(allOptions)
    setDefaultOption(defaultOptions)
    if (_.isEmpty(groupingOptions))
      setGroupingOption(defaultOptions)
    setCreateVppForm(vppForm(programList))
  }, [programList])

  useEffect(() => {
    if (_.isEmpty(stateList)) {
      setStateOptions([])
    } else if (_.isEmpty(programId)) {
      const options = _.isObject(stateList) ? Object.entries(stateList).map(([key, value]) => ({ label: key, value: `${value} - ${key}` })) : []
      setStateOptions(options)
    } else {
      const selectedProgram = programList.find(obj => obj.id === programId)
      const selectedProgramState = _.get(selectedProgram, 'state', '')
      const filteredOption = stateList[selectedProgramState]
      if (filteredOption === ALL) {
        let stateDropDownOptions = []
        Object.keys(stateList).forEach(state => {
          stateDropDownOptions.push({
            label: state,
            value: stateList[state]
          })
        })
        setStateOptions(stateDropDownOptions)
      } else {
        setStateOptions([{
          label: filteredOption ? selectedProgramState : ALL,
          value: filteredOption ? `${filteredOption} - ${selectedProgramState}` : ALL
        }])
      }
    }
  }, [stateList, programId, programList, props, program])

  useEffect(() => {
    if (programId) {
      setProgram(programList.find(obj => obj.id === programId))
      tagService.getTagByProgramId(programId, props.accountId)
        .then(response => {
          const defaultOptions = _.cloneDeep(defaultGroupingOptions)
          !_.isNull(response) && response.forEach((tag, index) => {
            if (index === 0) {
              defaultOptions.push({
                label: '-----------',
                value: '',
                disable: true
              })
            }
            defaultOptions.push({
              label: tag,
              value: tag,
            })
          })
          setGroupingOption(defaultOptions)
        })
        .catch(error => props.errorCb(SOMETHING_WENT_WRONG))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId])

  useEffect(() => {
    if (program) {
      utilService.getStateByCountry(program && program.country ? program.country : 'USA')
        .then(stateData => {
          setStateList({ ...stateData, ALL: ALL })
          setLoading(false)
        })
        .catch(error => {
          setLoading(false)
          setOpen(true)
          props.errorCb(_.get(error, 'data.error.displayMessage', 'Failed to fetch states'))
        })
    }
  }, [program])

  useEffect(() => {
    setProgram(programList.find(obj => obj.id === programId))
  }, [programList])

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" className={classes.checkbox} />
  const checkedIcon = <CheckBoxOutlinedIcon fontSize="small" className={classes.checkbox} />

  const schema = Yup.object({
    name: Yup.string().matches(/^[^;]*$/, 'semicolon not allowed in VPP name').required(),
    programId: Yup.string().required(),
    grpCriteria: Yup.array()
      .of(
        Yup.object().shape({
          grpKey: Yup.string().required(),
          grpValue: Yup
            .lazy((val, obj) => {
              const fieldObj = _.get(obj, 'parent', {})
              const key = _.get(fieldObj, 'grpKey', '')

              if (key === 'ZIP') {
                return Array.isArray(val)
                  ? Yup.array().of(Yup.string().matches(/^[^,]{1,20}(,[^,]{1,20})*$/, 'Enter comma-separated valid ZIP/Postal codes')).max(210, 'Max limit reached').required()
                  : Yup.string().matches(/^[^,]{1,20}(,[^,]{1,20})*$/, 'Enter comma-separated valid ZIP/Postal codes').max(210, 'Max limit reached').required()
              }
              if (key === 'SITE_IDS') {
                return Array.isArray(val)
                  ? Yup.array().of(Yup.string().matches(/^[0-9]{1,10}(,[0-9]{1,10})*$/, 'Enter comma-separated valid Site IDs')).required()
                  : Yup.string().matches(/^[0-9]{1,10}(,[0-9]{1,10})*$/, 'Enter comma-separated valid Site IDs').required()
              }
              if (key === 'CITY') {
                return Array.isArray(val)
                  ? Yup.array().of(Yup.string().matches(/^([A-Za-z][A-Za-z0-9_ ]*)(,[A-Za-z][A-Za-z0-9_ ]*)*$/, 'The city field must contain Alphabets. \nAlphanumeric values and underscore symbol are allowed.')).required()
                  : Yup.string().matches(/^([A-Za-z][A-Za-z0-9_ ]*)(,[A-Za-z][A-Za-z0-9_ ]*)*$/, 'The city field must contain Alphabets. \nAlphanumeric values and underscore symbol are allowed.').required()
              }
              return Array.isArray(val)
                ? Yup.array().of(Yup.string().matches(/^[A-Za-z0-9_,]*$/, 'Only alphabets, numbers, and underscore symbol are allowed')).required()
                : Yup.string().matches(/^[A-Za-z0-9_,]*$/, 'Only alphabets, numbers, and underscore symbol are allowed').required()
            })
        })
      )
  })

  const renderGroupingOption = (prevSelectedOptions) => {
    const grpOptions = _.cloneDeep(groupingOptions)
    grpOptions.forEach((obj, index) => {
      grpOptions[index].disable = prevSelectedOptions.some(list => list.grpKey === obj.value)
    })

    return grpOptions || []
  }

  const renderValues = (values) => {
    const modifiedValues = _.isArray(values) ? stateOptions.filter(obj => values.some(state => state === obj.label)) : []
    return modifiedValues
  }

  const renderIcon = (errors, value, index) => {
    if (value) {
      if (errors && errors.grpCriteria && errors.grpCriteria[index] && errors.grpCriteria[index].grpValue) {
        return <Icon src="circleCross" />
      } else {
        return <Icon src="circleTick" />
      }
    }

    return <></>
  }

  const initialValues = () => {
    const grpCriteria = _.get(props.vppDetails.query, 'tags', [])
    const listItem = Object.entries(grpCriteria).map(([key, value]) => ({ grpKey: key, grpValue: value }))

    return {
      name: props.isEdit ? _.get(props.vppDetails, 'name', '') : '',
      programId: props.isEdit ? _.get(props.vppDetails, 'program_id', '') : '',
      grpCriteria: props.isEdit ? listItem : [{ grpKey: '', grpValue: '' }]
    }
  }

  return (
    <Box>
      <Formik
        initialValues={initialValues()}
        enableReinitialize={true}
        validateOnMount={true}
        validationSchema={schema}
        onSubmit={(data) => {
          pushToDataLayer({ event: USERBEHAVIOURTRACKING, accountID: props.accountId, programID: _.get(data, 'programId', '') })
          const groupingTag = _.get(data, 'grpCriteria', [])
          const additionalProps = {}
          groupingTag.forEach((tagObj) => {
            additionalProps[tagObj.grpKey] = _.isArray(tagObj.grpValue) ? tagObj.grpValue : (tagObj.grpValue).split(',').map(str => str.trim())
          })

          const params = {
            account_id: props.accountId,
            name: _.get(data, 'name', ''),
            program_id: _.get(data, 'programId', ''),
            query: {
              operator: "AND",
              tags: additionalProps
            }
          }

          setLoading(true)

          if (props.isEdit) {
            vppService.updateVpp(_.get(props.vppDetails, 'id', ''), params, props.accountId)
              .then(response => {
                setLoading(false)
                props.successCb()
              })
              .catch(error => {
                setLoading(false)
                const data = _.get(error, 'data', {})
                const errorObj = _.get(data, 'error', {})
                const displayMessage = _.get(errorObj, 'displayMessage', SOMETHING_WENT_WRONG)
                props.errorCb(displayMessage)
              })
          } else {
            vppService.createVpp(params, props.accountId)
              .then(response => {
                setLoading(false)
                props.successCb()
              })
              .catch(error => {
                setLoading(false)
                const data = _.get(error, 'data', {})
                const errorObj = _.get(data, 'error', {})
                const displayMessage = _.get(errorObj, 'displayMessage', SOMETHING_WENT_WRONG)
                props.errorCb(displayMessage)
              })
          }
        }
        }
        render={({ values, setFieldValue, handleChange, isValid, dirty, errors, touched }) => {
          return (
            <Form>
              <Box className={classes.wrapper}>
                <Box className={classes.formWrapper}>
                  {createVppForm.map((obj) => (
                    <React.Fragment>
                      {
                        obj.type === "select"
                          ? (
                            <TextField
                              select
                              id={obj.id}
                              name={obj.name}
                              label={obj.label}
                              options={obj.options}
                              disabled={props.isViewOnly}
                              className={classes.field}
                              InputProps={{
                                classes: { root: classes.input, underline: classes.underline }
                              }}
                              SelectProps={{
                                MenuProps: {
                                  classes: { paper: classes.paper },
                                  anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left"
                                  },
                                  getContentAnchorEl: null
                                }
                              }}
                              value={values[obj.id]}
                              onChange={(event) => {
                                const newInputValue = event.target.value || ''
                                setProgramId(newInputValue)
                                setFieldValue(obj.name, newInputValue)
                                const program = programList.find(program => program.id === event.target.value)
                                setLoading(true)
                                utilService.getStateByCountry(program && program.country ? program.country : 'USA')
                                  .then(stateData => {
                                    setStateList({ ...stateData, ALL: ALL })
                                    if (_.isEmpty(stateData)) {
                                      setFieldValue('state', ALL)
                                    }
                                    setLoading(false)
                                  })
                                  .catch(error => {
                                    setLoading(false)
                                    setOpen(true)
                                    props.errorCb(_.get(error, 'data.error.displayMessage', 'Failed to fetch states'))
                                  })
                              }}
                            >
                              {obj.options.map(option => {
                                return <MenuItem value={option.id}>{_.get(option, 'program_name_admin_ui', '')}</MenuItem>
                              })}
                            </TextField>
                          )
                          : obj.type === 'fieldArray'
                            ? (
                              <Box className={classes.groupingWrapper}>
                                <Typography className={classes.groupingLabel}>Grouping Criteria</Typography>
                                <Typography className={classes.note}>* Enter multiple values separated by commas</Typography>
                                <Box className={classes.fieldArrayWrapper}>
                                  <FieldArray name="grpCriteria">
                                    {({ push, remove }) => (
                                      <div>
                                        {values.grpCriteria.map((p, index) => {
                                          const groupingKey = `grpCriteria[${index}].grpKey`
                                          const groupingValue = `grpCriteria[${index}].grpValue`
                                          return (
                                            <Box>
                                              <Box key={index} className={classes.fieldRow}>
                                                <TextField
                                                  select
                                                  name={groupingKey}
                                                  className={classes.field}
                                                  options={renderGroupingOption(values.grpCriteria)}
                                                  disabled={_.isEmpty(programId) || props.isViewOnly}
                                                  InputProps={{
                                                    classes: { root: classes.fieldInput, underline: classes.underline }
                                                  }}
                                                  SelectProps={{
                                                    MenuProps: {
                                                      classes: { paper: classes.paper },
                                                      anchorOrigin: {
                                                        vertical: "bottom",
                                                        horizontal: "left"
                                                      },
                                                      getContentAnchorEl: null
                                                    }
                                                  }}
                                                  value={p.grpKey}
                                                  onChange={(e) => {
                                                    setFieldValue(groupingKey, e.target.value)
                                                    setFieldValue(groupingValue, '')
                                                  }}
                                                  style={{ width: '20%' }}
                                                >
                                                  {renderGroupingOption(values.grpCriteria).map((option) => {
                                                    return <MenuItem disabled={option.disable ? option.disable : false} value={option.value} classes={{ root: `${classes.options} ${_.isEmpty(option.value) ? classes.disable : ''}` }}>{option.label}</MenuItem>
                                                  })}
                                                </TextField>
                                                <Typography>=</Typography>
                                                {
                                                  p.grpKey === 'STATE'
                                                    ? (
                                                      <Field
                                                        name="autocomplete"
                                                        component={Autocomplete}
                                                        open={open}
                                                        multiple
                                                        disabled={_.isEmpty(programId)}
                                                        onChange={(event, newInputValue) => {
                                                          const modifiedValue = newInputValue.map(list => list.label)
                                                          setFieldValue(groupingValue, modifiedValue)
                                                        }}
                                                        value={renderValues(p.grpValue)}
                                                        onOpen={() => setOpen(true)}
                                                        onBlur={() => setOpen(false)}
                                                        classes={{ option: classes.options, paper: classes.stateDropDown }}
                                                        options={stateOptions}
                                                        getOptionLabel={(option) => option.value}
                                                        getOptionDisabled={(option) => {
                                                          if (p.grpValue.includes(ALL)) {
                                                            return option.value !== ALL
                                                          }
                                                          if (p.grpValue.length > 0 && !p.grpValue.includes(ALL)) {
                                                            return option.value === ALL
                                                          }
                                                        }}
                                                        renderOption={(option, { selected }) => (
                                                          <Box className={classes.autocompleteOptions}>
                                                            <Checkbox
                                                              icon={icon}
                                                              checkedIcon={checkedIcon}
                                                              style={{ marginRight: 8, padding: 0 }}
                                                              checked={selected}
                                                            />
                                                            <Typography>{option.value}</Typography>
                                                          </Box>
                                                        )}
                                                        renderInput={(params) => {
                                                          return <TextField
                                                            {...params}
                                                            name={groupingValue}
                                                            disabled={props.isViewOnly}
                                                          />
                                                        }}
                                                        renderTags={(tags) => {
                                                          return <Box className={classes.tagWrapper}>
                                                            {tags.map((tag, index) => (
                                                              <Typography>{`${index > 0 ? ', ' : ''}${tag.label}`}</Typography>
                                                            ))}
                                                          </Box>
                                                        }}
                                                      />
                                                    )
                                                    : (
                                                      <TextField
                                                        name={groupingValue}
                                                        className={classes.field}
                                                        disabled={_.isEmpty(programId) || props.isViewOnly}
                                                        FormHelperTextProps={{
                                                          classes: { filled: classes.error }
                                                        }}
                                                        InputProps={{
                                                          classes: { root: classes.fieldInput, underline: classes.underline },
                                                          endAdornment: renderIcon(errors, p.grpValue, index),
                                                        }}
                                                        value={p.grpValue}
                                                        onBlur={(e) => {
                                                          if (p.grpKey === 'CITY') {
                                                            let value = e.target.value?.split(',')
                                                            value = value?.map(val => val.trim())
                                                            value = value?.join(',')
                                                            setFieldValue(groupingValue, value)
                                                          }
                                                        }}
                                                        onChange={(event) => {
                                                          const newInputValue = event.target.value || ''
                                                          setFieldValue(groupingValue, newInputValue)
                                                        }}
                                                      />
                                                    )
                                                }
                                                <IconButton className={`${classes.icon} ${groupingOptions.length === 3 && values.grpCriteria.length === groupingOptions.length ? classes.hide : groupingOptions.length !== 3 && values.grpCriteria.length === groupingOptions.length - 1 ? classes.hide : ''}`} disabled={_.isEmpty(programId) || props.isViewOnly} onClick={() => push({
                                                  grpKey: "",
                                                  grpValue: ""
                                                })}>
                                                  <AddCircleOutlineRoundedIcon />
                                                </IconButton>
                                                <IconButton aria-label="delete" className={`${classes.icon} ${values.grpCriteria.length > 1 ? '' : classes.hide}`} disabled={_.isEmpty(programId) || props.isViewOnly} onClick={() => remove(index)}>
                                                  <RemoveCircleOutlineIcon />
                                                </IconButton>
                                              </Box>
                                              {p.grpValue && errors && errors.grpCriteria && errors.grpCriteria[index] && errors.grpCriteria[index].grpValue && <Typography className={classes.error}>{errors.grpCriteria[index].grpValue}</Typography>}
                                            </Box>
                                          )
                                        })}

                                      </div>
                                    )
                                    }
                                  </FieldArray>
                                </Box>
                              </Box>
                            )
                            : (
                              <TextField
                                id={obj.id}
                                name={obj.name}
                                disabled={props.isViewOnly}
                                error={values[obj.id] && errors[obj.id] ? true : false}
                                helperText={values[obj.id] && errors[obj.id]}
                                label={obj.label}
                                className={classes.field}
                                FormHelperTextProps={{
                                  classes: { filled: classes.error }
                                }}
                                InputProps={{
                                  classes: { root: classes.input, underline: classes.underline },
                                  type: obj.type === 'number' ? 'number' : 'text',
                                }}
                                onInput={(e) => {
                                  e.target.value = obj.id === 'maxEnrollment' && e.target.value !== '' ? Math.max(0, parseInt(e.target.value)).toString().slice(0, 12) : e.target.value
                                }}
                                InputLabelProps={{ classes: { root: classes.input, shrink: classes.shrink } }}
                                value={values[obj.id]}
                                onChange={handleChange}
                                onBlur={(e) => obj.id === 'name' && setFieldValue(obj.id, e.target.value.trim())}
                              />
                            )
                      }
                    </React.Fragment>
                  ))}
                </Box>
                <Box className={classes.groupContainer}>
                  <GroupingCriteria programId={values.programId || ''} grpCriteria={values.grpCriteria || []} accountId={props.accountId} errors={errors} {...props} />
                </Box>
              </Box>
              {!props.isViewOnly &&
                <>
                  <hr className={classes.hr} />
                  <Box className={classes.buttonWrapper}>
                    <AppButton text='Save' type="submit" className={`${classes.button} ${dirty ? isValid ? '' : classes.disable : classes.disable} `} />
                  </Box>
                </>
              }
            </Form>
          )
        }}
      />
      <Loader loading={loading} />
    </Box>
  )
}

VppForm.default = {
  stateList: [],
  currentUser: {},
  vppDetails: {},
  isEdit: false,
  successCb: () => { },
  errorCb: () => { }
}

export default VppForm